import { Link, useRouterState } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState } from 'react';

import { useMe } from '@/lib/auth/use-me';
import { isNativeApp } from '@/lib/platform';
import { useMarket } from '@/lib/market/market-context';

import { useSignOut } from './session';

const AUTH_PATHS: Record<string, true> = {
  '/login': true,
  '/register': true,
  '/forgot-password': true,
  '/reset-password': true,
};

/**
 * TopNav 右上角：載入中是一顆灰色骨架、未登入是「登入」膠囊、已登入是頭像選單
 * （我的自選股／帳號設定／登出）。Escape 與點選外面都會關閉，關閉後焦點回到頭像按鈕。
 */
export function UserMenu() {
  const { me, isLoading } = useMe();
  const { market } = useMarket();
  const signOut = useSignOut();
  const location = useRouterState({ select: (state) => state.location });
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const close = (restoreFocus: boolean) => {
      setOpen(false);
      if (restoreFocus) buttonRef.current?.focus();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close(true);
    };
    const onPointer = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      close(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  if (isLoading) {
    return <span className="block h-7 w-7 animate-pulse rounded-full bg-surface-3" aria-hidden="true" />;
  }

  if (me === null) {
    // 已經在認證頁上就不再帶 redirect（否則登入完會回到登入頁）。
    const onAuthPage = Object.hasOwn(AUTH_PATHS, location.pathname);
    return (
      <Link
        to="/login"
        search={onAuthPage ? {} : { redirect: location.href }}
        className="flex h-10 items-center glass rounded-full px-3 text-12 font-semibold text-fg-1 transition-colors sm:h-8"
      >
        登入
      </Link>
    );
  }

  const label = me.display_name ?? me.email;
  const initial = (label.trim()[0] ?? '?').toUpperCase();

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`帳號選單：${label}`}
        onClick={() => {
          setOpen((value) => !value);
        }}
        className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-700 text-11 font-bold text-white shadow-xs transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
      >
        {initial}
      </button>
      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="帳號"
          className="absolute right-0 top-9 z-40 w-56 glass-strong overflow-hidden rounded-[14px] py-1"
        >
          <div className="border-b border-border-1 px-3 py-2">
            {me.display_name === null ? null : (
              <p className="truncate text-13 font-semibold text-fg-1">{me.display_name}</p>
            )}
            <p className="truncate text-12 text-muted">{me.email}</p>
          </div>
          <Link
            role="menuitem"
            to="/$market/watchlist"
            params={{ market }}
            onClick={() => {
              setOpen(false);
            }}
            className="flex items-center gap-2 px-3 py-2 text-13 text-fg-1 hover:bg-surface-2 focus:bg-surface-2 focus:outline-none"
          >
            <span aria-hidden="true">⭐</span>
            我的自選股
          </Link>
          {me.entitlements.some((e) => e === 'research' || e === 'admin') ? (
            <Link
              role="menuitem"
              to="/$market/research"
              params={{ market }}
              onClick={() => {
                setOpen(false);
              }}
              className="flex items-center gap-2 px-3 py-2 text-13 text-fg-1 hover:bg-surface-2 focus:bg-surface-2 focus:outline-none"
            >
              <span aria-hidden="true">🔬</span>
              內部研究
            </Link>
          ) : null}
          {me.entitlements.includes('admin') && !isNativeApp() ? (
            <Link
              role="menuitem"
              to="/admin"
              onClick={() => {
                setOpen(false);
              }}
              className="flex items-center gap-2 px-3 py-2 text-13 text-fg-1 hover:bg-surface-2 focus:bg-surface-2 focus:outline-none"
            >
              <span aria-hidden="true">🛠</span>
              後台管理
            </Link>
          ) : null}
          <Link
            role="menuitem"
            to="/account"
            onClick={() => {
              setOpen(false);
            }}
            className="flex items-center gap-2 px-3 py-2 text-13 text-fg-1 hover:bg-surface-2 focus:bg-surface-2 focus:outline-none"
          >
            <span aria-hidden="true">⚙</span>
            帳號設定
          </Link>
          <button
            type="button"
            role="menuitem"
            disabled={signingOut}
            onClick={() => {
              setSigningOut(true);
              void signOut().finally(() => {
                setSigningOut(false);
                setOpen(false);
              });
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-13 text-fg-1 hover:bg-surface-2 focus:bg-surface-2 focus:outline-none disabled:opacity-60"
          >
            <span aria-hidden="true">↩</span>
            {signingOut ? '登出中…' : '登出'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
